import { ref, computed, type Ref } from "vue";
import {
  type Reservation,
  type ReservationRequest,
  type TableStatus,
  ACTIVE_TABLE_STATUSES,
} from "@lemmonhq/types";
import { timeWindowsOverlap } from "@lemmonhq/blogic";
import { useMutation } from "@tanstack/vue-query";
import { useTrpc } from "../../../providers";
import { useToast } from "../../../providers/Toast";
import { useLocalization } from "@lemmonhq/uikit";
import {
  isConfirmedStatus,
  isSeatedStatus,
  isActiveTableStatus,
  reservationsForTable,
} from "../../../utils/reservationStatus";
import { type DropModeState } from "../tables-types";

export const emptyDrop = (active: boolean): DropModeState => ({
  active,
  reservation: null,
  request: null,
  selectedTableIds: [],
  intent: undefined,
});

export function useReservationDropMode(
  tables: Ref<TableStatus[]>,
  floorPlans: Ref<any[]>,
  displayedReservations: Ref<Reservation[]>,
  isSelectedToday: Ref<boolean>,
  refreshTablesReservations: () => Promise<void>,
  refreshShop: () => void,
  refreshTables: () => Promise<void>,
  handleFloorPlanSelect: (id: string) => void,
  currentView: Ref<string>,
  floorPlanViewMode: Ref<string>,
) {
  const trpc = useTrpc();
  const toast = useToast();
  const { t } = useLocalization();

  const dropModeState = ref<DropModeState>(emptyDrop(false));
  const viewingReservation = ref<Reservation | null>(null);
  const viewingRequest = ref<ReservationRequest | null>(null);
  const isConfirmingDropMode = ref(false);

  const acceptRequestMutation = useMutation({
    mutationFn: (input: { request_id: string; table_ids: number[] }) =>
      trpc.reservations.acceptRequest.mutate(input),
    onSuccess: refreshTablesReservations,
  });

  const updateReservationTablesMutation = useMutation({
    mutationFn: (input: { reservation_id: string; table_ids: number[] }) =>
      trpc.reservations.updateTables.mutate(input),
    onSuccess: refreshTablesReservations,
  });

  const seatReservationMutation = useMutation({
    mutationFn: (input: {
      reservation_id: string;
      tables: Array<{ table_id: number }>;
    }) => trpc.reservations.seat.mutate(input),
    onSuccess: refreshTablesReservations,
  });

  const loadingReservationCollections = computed(
    () =>
      acceptRequestMutation.isPending.value ||
      updateReservationTablesMutation.isPending.value ||
      seatReservationMutation.isPending.value,
  );

  const activeTarget = () => {
    const d = dropModeState.value;
    if (d.request) {
      return {
        id: d.request.id,
        timeIso: d.request.requested_time,
        durationMins: d.request.duration_mins,
        partySize: d.request.party_size,
        partyName: d.request.party_name,
        isRequest: true,
      };
    }
    if (d.reservation) {
      return {
        id: d.reservation.id,
        timeIso: d.reservation.time,
        durationMins: d.reservation.duration_mins,
        partySize: d.reservation.party_size,
        partyName: d.reservation.party_name,
        isRequest: false,
      };
    }
    return null;
  };

  const handleEnterDropMode = (
    reservation: Reservation,
    intentArg?: "seat" | "assign",
  ) => {
    const assignedIds = [
      ...new Set(
        (reservation.table_assignments || []).map((a) => Number(a.table_id)),
      ),
    ];

    if (currentView.value !== "floor_plan") {
      currentView.value = "floor_plan";
    }

    if (assignedIds.length > 0) {
      const targetTableId = Number(assignedIds[0]);
      const roomWithTable = floorPlans.value.find((fp) =>
        fp.plan.some((p: any) => Number(p.tableId) === targetTableId),
      );
      if (roomWithTable) {
        handleFloorPlanSelect(roomWithTable.id);
        floorPlanViewMode.value = "selected";
      }
    }

    const hasTable = assignedIds.length > 0;
    const intent =
      intentArg ?? (hasTable && isSelectedToday.value ? "seat" : "assign");

    dropModeState.value = {
      active: true,
      reservation,
      request: null,
      selectedTableIds: [...assignedIds],
      intent,
    };
  };

  const handleEnterDropModeForRequest = (request: ReservationRequest) => {
    if (currentView.value !== "floor_plan") currentView.value = "floor_plan";
    dropModeState.value = {
      active: true,
      reservation: null,
      request,
      selectedTableIds: [],
      intent: "assign",
    };
  };

  const handleExitDropMode = () => {
    dropModeState.value = emptyDrop(true);
  };

  const handleSelectReservation = (
    reservation: Reservation,
    onCloseSidebar: () => void,
  ) => {
    onCloseSidebar();
    if (isSeatedStatus(reservation.status)) {
      viewingReservation.value = reservation;
    } else {
      handleEnterDropMode(reservation);
    }
  };

  const handleSelectRequest = (
    request: ReservationRequest,
    onCloseSidebar: () => void,
  ) => {
    onCloseSidebar();
    handleEnterDropModeForRequest(request);
  };

  const handleShowReservationDetail = (reservation: Reservation) => {
    viewingReservation.value = reservation;
  };

  const handleShowRequestDetail = (request: ReservationRequest) => {
    viewingRequest.value = request;
  };

  const handleChangeTables = (reservation: Reservation) => {
    viewingReservation.value = null;
    handleEnterDropMode(reservation);
  };

  const handleTableClickInDropMode = (tableId: number) => {
    if (!dropModeState.value.reservation && !dropModeState.value.request) {
      const tableReservations = reservationsForTable(
        displayedReservations.value,
        tableId,
        ACTIVE_TABLE_STATUSES,
      );
      if (tableReservations.length === 0) return;
      if (tableReservations.length === 1) {
        viewingReservation.value = tableReservations[0];
        return;
      }
      const tbl = tables.value.find((t) => Number(t.id) === tableId);
      return {
        tableId,
        tableName: tbl?.table?.name || `#${tableId}`,
      };
    }

    const current = dropModeState.value.selectedTableIds;
    const idx = current.indexOf(tableId);
    if (idx >= 0) {
      dropModeState.value.selectedTableIds = current.filter(
        (id) => id !== tableId,
      );
      return;
    }

    const tableStatus = tables.value.find((t) => t.table.id === tableId);
    const target = activeTarget()!;
    const resStart = new Date(target.timeIso).getTime();
    const resEnd = resStart + target.durationMins * 60000;

    const isBusy = isSelectedToday.value && tableStatus?.table.is_busy;

    const conflicting = displayedReservations.value.find(
      (r) =>
        r.id !== target.id &&
        isActiveTableStatus(r.status) &&
        r.table_assignments?.some((a) => Number(a.table_id) === tableId) &&
        timeWindowsOverlap(
          { startMs: resStart, endMs: resEnd },
          {
            startMs: new Date(r.time).getTime(),
            endMs: new Date(r.time).getTime() + r.duration_mins * 60000,
          },
        ),
    );

    if (isBusy || conflicting) {
      const reason = isBusy
        ? "This table is currently busy."
        : `This table has a reservation for ${conflicting!.party_name} in the same time period.`;
      if (!window.confirm(`${reason}\n\nDo you want to select it anyway?`)) {
        return;
      }
    }

    const tableCapacity = (tableStatus?.table as any)?.capacity || 0;
    if (tableCapacity > 0 && target.partySize > tableCapacity) {
      const totalSelected = [...current, tableId];
      const totalCapacity = totalSelected.reduce((sum, id) => {
        const ts = tables.value.find((t) => t.table.id === id);
        return sum + ((ts?.table as any)?.capacity || 0);
      }, 0);
      if (target.partySize > totalCapacity) {
        if (
          !window.confirm(
            `Party size is ${target.partySize} but selected tables only seat ${totalCapacity}.\n\nDo you want to select this table anyway?`,
          )
        ) {
          return;
        }
      }
    }

    dropModeState.value.selectedTableIds = [...current, tableId];
    return undefined;
  };

  const handleConfirmDropMode = async (actionOverride?: "seat" | "assign") => {
    if (isConfirmingDropMode.value) return;
    const { reservation, request, selectedTableIds, intent } =
      dropModeState.value;
    if (selectedTableIds.length === 0) return;

    const willAssign = request
      ? true
      : actionOverride
        ? actionOverride === "assign"
        : intent === "assign";

    isConfirmingDropMode.value = true;
    try {
      if (request) {
        await acceptRequestMutation.mutateAsync({
          request_id: request.id,
          table_ids: selectedTableIds,
        });
        await Promise.all([refreshTables(), refreshTablesReservations()]);
        handleExitDropMode();
        refreshShop();
        toast.showToast(t("reservations.table_popup.tables_assigned_success"), {
          type: "success",
        });
        return;
      }

      if (!reservation) return;
      if (willAssign) {
        await updateReservationTablesMutation.mutateAsync({
          reservation_id: reservation.id,
          table_ids: selectedTableIds,
        });
      } else {
        await seatReservationMutation.mutateAsync({
          reservation_id: reservation.id,
          tables: selectedTableIds.map((id) => ({ table_id: id })),
        });
      }
      await Promise.all([refreshTables(), refreshTablesReservations()]);
      handleExitDropMode();
      refreshShop();
      toast.showToast(
        willAssign
          ? t("reservations.table_popup.tables_assigned_success")
          : t("reservations.table_popup.reservation_seated"),
        { type: "success" },
      );
    } catch (error) {
      console.error("Failed to confirm reservation tables:", error);
      toast.showToast(
        willAssign
          ? t("reservations.table_popup.tables_assign_failed")
          : t("reservations.table_popup.seat_failed"),
        { type: "error" },
      );
    } finally {
      isConfirmingDropMode.value = false;
    }
  };

  return {
    dropModeState,
    viewingReservation,
    viewingRequest,
    loadingReservationCollections,
    handleEnterDropMode,
    handleEnterDropModeForRequest,
    handleExitDropMode,
    handleSelectReservation,
    handleSelectRequest,
    handleShowReservationDetail,
    handleShowRequestDetail,
    handleChangeTables,
    handleTableClickInDropMode,
    handleConfirmDropMode,
    emptyDrop,
  };
}
